import { c as _c } from "react/compiler-runtime";
import { ApiClient } from '@/services/api-client';
import { useAuth } from '@clerk/nextjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Which Bily lifecycle events this organization already reported.
 *
 * Bily does not deduplicate and has no server-side ingestion API, so the
 * browser is the only thing that can report an event, and a browser only knows
 * what *it* sent. Keeping "already reported" in localStorage therefore meant a
 * customer who opens the app on a phone and then a laptop reported the same
 * payment twice. The backend holds the marks per org instead, which also
 * survives a cleared browser.
 */

const MARKS_QUERY_KEY = 'organization.analyticsMarks';
export type BilyMarks = {
  /** The query settled, either way. Nothing reports before this. */
  isLoaded: boolean;
  /**
   * The endpoint answered. False on 404 (a backend deployed before the marks
   * existed) or on any error: without the marks we cannot deduplicate, and an
   * event we might send twice is worse than one we do not send.
   */
  isAvailable: boolean;
  /**
   * Whether this org is young enough to report Activated and Trial Started.
   * Orgs created before the backend cutoff predate the funnel; reporting them
   * would date a wave of old conversions to today. Deliberately NOT applied to
   * money events: a real payment is worth reporting whatever the org's age.
   */
  lifecycleEnabled: boolean;
  has: (key: string) => boolean;
  /**
   * Emit once, ever, for this org. `emit` runs synchronously, then the mark is
   * written.
   *
   * That order is on purpose. If the mark went first and the Bily script were
   * blocked by an ad blocker, the event would be lost for good with the org
   * marked as done. This way a tab that dies between the two can at worst
   * re-send once on the next visit. A rare duplicate beats a silent loss.
   */
  report: (key: string, emit: () => void) => void;
};
export function useBilyAnalyticsMarks() {
  const $ = _c(24);
  const {
    orgId
  } = useAuth();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const inFlight = useRef(t0);
  let t1;
  if ($[1] !== orgId) {
    t1 = [MARKS_QUERY_KEY, orgId];
    $[1] = orgId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !!orgId;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: _temp,
      enabled: t2,
      staleTime: 60000,
      retry: false,
      refetchOnWindowFocus: false
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const query = useQuery(t3);
  query.data?.ReportedKeys;
  let t4;
  if ($[6] !== query.data?.ReportedKeys) {
    t4 = query.data?.ReportedKeys ?? [];
    $[6] = query.data?.ReportedKeys;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = new Set(t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const reportedKeys = t5;
  const isLoaded = !!orgId && !query.isPending;
  const isAvailable = query.isSuccess;
  let t6;
  if ($[10] !== reportedKeys) {
    t6 = key => reportedKeys.has(key);
    $[10] = reportedKeys;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const has = t6;
  let t7;
  if ($[12] !== isAvailable || $[13] !== isLoaded || $[14] !== orgId || $[15] !== queryClient || $[16] !== reportedKeys) {
    t7 = (key_0, emit) => {
      if (!isLoaded || !isAvailable) {
        return;
      }
      if (reportedKeys.has(key_0) || inFlight.current.has(key_0)) {
        return;
      }
      inFlight.current.add(key_0);
      emit();
      ApiClient.organization.markAnalyticsKey(key_0).then(response => {
        queryClient.setQueryData([MARKS_QUERY_KEY, orgId], response.data);
      }).catch(error => {
        inFlight.current.delete(key_0);
        console.error("[Bily] failed to mark", key_0, error);
      });
    };
    $[12] = isAvailable;
    $[13] = isLoaded;
    $[14] = orgId;
    $[15] = queryClient;
    $[16] = reportedKeys;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const report = t7;
  const lifecycleEnabled = query.data?.LifecycleReportingEnabled ?? false;
  let t8;
  if ($[18] !== has || $[19] !== isAvailable || $[20] !== isLoaded || $[21] !== lifecycleEnabled || $[22] !== report) {
    t8 = {
      isLoaded,
      isAvailable,
      lifecycleEnabled,
      has,
      report
    };
    $[18] = has;
    $[19] = isAvailable;
    $[20] = isLoaded;
    $[21] = lifecycleEnabled;
    $[22] = report;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
async function _temp() {
  return (await ApiClient.organization.getAnalyticsMarks()).data;
}